/**
 * HoursStepper — горизонтальный stepper часов с шагом 0.5.
 *
 * Используется в двух местах (§3.6 / §4.2):
 *  - `NewEntryFlow` шаг 4 (§4.2) — `value=null` стартовое, plus устанавливает
 *    0.5; пока не выбрано — нельзя сабмитить.
 *  - `FieldSignPage` — стартовое значение = `default_declared_hours_half`
 *    (учитывает сокращённый день).
 *
 * Идиома плиток (`--tile-*` токены) общая с `NumericPad` — те же hard-edge
 * плунжеры, ничего не прыгает. Квик-сет [1..8] под центром — целые часы
 * пересиливают текущее значение, без накопления.
 *
 * `step = 0.5` — единственный поддерживаемый. На бэк уходит `hours_half`
 * (целое); конверсия `value * 2` происходит в родителе.
 */
import * as React from "react";
import { Minus, Plus } from "lucide-react";
import { cn } from "@/lib/utils";

const STEP = 0.5;
const QUICK_SET: readonly number[] = [1, 2, 3, 4, 5, 6, 7, 8] as const;

export interface HoursStepperProps {
  value: number | null;
  onChange: (next: number) => void;
  /** Минимально допустимое значение (включительно). По умолчанию 0.5. */
  min?: number;
  /** Максимум (включительно). По умолчанию 24. */
  max?: number;
  /** Плейсхолдер цифры при `value=null`. По умолчанию «— ч». */
  placeholder?: string;
  /** Лейбл единицы (по умолчанию «ч»). */
  unit?: string;
  /** Aria-label на корневой группе. */
  "aria-label"?: string;
  /** data-testid на корень. */
  "data-testid"?: string;
  "aria-invalid"?: boolean;
  "aria-describedby"?: string;
  disabled?: boolean;
  /** Ряд quick-set 1..8 под стиппером. По умолчанию `true`. */
  showQuickSet?: boolean;
}

function roundHalf(n: number): number {
  return Math.round(n * 2) / 2;
}

export function HoursStepper({
  value,
  onChange,
  min = 0.5,
  max = 24,
  placeholder = "— ч",
  unit = "ч",
  "aria-label": ariaLabel,
  "data-testid": testId = "hours-stepper",
  "aria-invalid": ariaInvalid,
  "aria-describedby": ariaDescribedBy,
  disabled = false,
  showQuickSet = true,
}: HoursStepperProps): React.ReactElement {
  const isUnset = value === null;
  const canDec = !disabled && !isUnset && (value as number) - STEP >= min;
  const canInc = !disabled && (isUnset || (value as number) + STEP <= max);

  const handleDec = (): void => {
    if (!canDec || isUnset) return;
    onChange(roundHalf((value as number) - STEP));
  };
  const handleInc = (): void => {
    if (!canInc) return;
    const next = isUnset ? STEP : roundHalf((value as number) + STEP);
    onChange(Math.min(next, max));
  };
  const handleQuickSet = (n: number): void => {
    if (disabled) return;
    onChange(Math.min(Math.max(n, min), max));
  };

  // Display: формат «3.0» / «3.5» (одна десятичная), «4» → «4.0» — чтобы
  // не было визуального прыжка ширины при тикании ±0.5.
  const display = isUnset ? placeholder : (value as number).toFixed(1);

  return (
    <div
      role="group"
      aria-label={ariaLabel ?? "Часы работы"}
      data-testid={testId}
      className="flex flex-col items-stretch gap-3"
    >
      {/* Главная строка: − [big display] + */}
      <div className="flex items-center justify-center gap-3">
        <StepperTile
          ariaLabel="Уменьшить"
          onPress={handleDec}
          disabled={!canDec}
          testId={`${testId}-dec`}
        >
          <Minus className="h-7 w-7" aria-hidden />
        </StepperTile>

        <div
          className="flex min-w-[140px] flex-1 items-baseline justify-center gap-1.5 px-3 py-2"
          data-testid={`${testId}-value`}
          style={{
            background: "var(--bg-panel-0)",
            border: "1px solid var(--rule-line)",
            borderRadius: "var(--tile-radius)",
            boxShadow: "var(--shadow-plate)",
            minHeight: "var(--tile-size)",
          }}
        >
          <span
            className="font-display font-semibold leading-none"
            style={{
              color: isUnset ? "var(--fg-3)" : "var(--fg-1)",
              fontFamily: "var(--font-display)",
              fontFeatureSettings: '"tnum" 1, "lnum" 1',
              fontSize: "var(--fs-40, 40px)",
              letterSpacing: "0.01em",
            }}
          >
            {display}
          </span>
          {!isUnset && (
            <span
              className="font-condensed text-[13px] uppercase tracking-[0.18em]"
              style={{ color: "var(--fg-3)", fontFamily: "var(--font-display)" }}
            >
              {unit}
            </span>
          )}
        </div>

        <StepperTile
          ariaLabel="Увеличить"
          onPress={handleInc}
          disabled={!canInc}
          testId={`${testId}-inc`}
          ariaInvalid={ariaInvalid}
          ariaDescribedBy={ariaDescribedBy}
        >
          <Plus className="h-7 w-7" aria-hidden />
        </StepperTile>
      </div>

      {/* Quick-set row 1..8 — overflow-x-auto если не помещается */}
      {showQuickSet && (
        <div
          className="-mx-1 flex items-stretch justify-between gap-1 overflow-x-auto px-1 pb-0.5"
          data-testid={`${testId}-quickset`}
        >
          {QUICK_SET.map((n) => {
            const isActive = value === n;
            const inRange = n >= min && n <= max;
            return (
              <button
                key={n}
                type="button"
                disabled={disabled || !inRange}
                onClick={() => handleQuickSet(n)}
                aria-label={`Поставить ${n} часов`}
                aria-pressed={isActive}
                data-testid={`${testId}-quick-${n}`}
                data-active={isActive || undefined}
                className={cn(
                  "relative flex shrink-0 select-none items-center justify-center font-mono font-medium",
                  "transition-[background-color,box-shadow,transform] duration-75",
                  "disabled:cursor-not-allowed disabled:opacity-40 active:translate-y-[1px]",
                )}
                style={{
                  flex: "1 1 0",
                  minWidth: 36,
                  height: 44,
                  borderRadius: "var(--tile-radius)",
                  fontSize: "var(--fs-18, 18px)",
                  fontFeatureSettings: '"tnum" 1, "lnum" 1',
                  // §20/8.3: active = оранжевый (--accent-agent = heat-bright).
                  // Раньше был чёрный --soot-900; Антон хотел тёплый highlight
                  // как у активного шага в other UI. Бренд-токен, не сырой
                  // bg-orange-500.
                  background: isActive ? "var(--accent-agent)" : "var(--bg-panel-0)",
                  color: isActive ? "#fff" : "var(--fg-1)",
                  border: isActive ? "1px solid var(--accent-agent)" : "1px solid var(--rule-line)",
                  boxShadow: isActive ? "1px 1px 0 var(--heat-deep)" : "var(--shadow-plate)",
                }}
              >
                {n}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

interface StepperTileProps {
  ariaLabel: string;
  onPress: () => void;
  disabled?: boolean;
  testId?: string;
  ariaInvalid?: boolean;
  ariaDescribedBy?: string;
  children: React.ReactNode;
}

function StepperTile({
  ariaLabel,
  onPress,
  disabled = false,
  testId,
  ariaInvalid,
  ariaDescribedBy,
  children,
}: StepperTileProps): React.ReactElement {
  return (
    <button
      type="button"
      onClick={onPress}
      disabled={disabled}
      aria-label={ariaLabel}
      aria-invalid={ariaInvalid}
      aria-describedby={ariaDescribedBy}
      id={testId}
      data-testid={testId}
      className={cn(
        "relative flex shrink-0 select-none items-center justify-center",
        "transition-[background-color,box-shadow,transform] duration-75",
        "disabled:cursor-not-allowed disabled:opacity-40 active:translate-y-[1px]",
      )}
      style={{
        width: "var(--tile-size)",
        height: "var(--tile-size)",
        borderRadius: "var(--tile-radius)",
        color: "var(--fg-1)",
        background: "var(--bg-panel-0)",
        border: "1px solid var(--rule-line)",
        boxShadow: "var(--shadow-plate)",
      }}
    >
      {children}
    </button>
  );
}
